import { useEffect, useId, useState, type ReactElement } from "react";
import type { RenderPurchaseWire } from "@shared/api";
import { api } from "../../api/client";
import downloadIcon from "../icons/16-Download.svg";
import type { InspectorPresentation } from "./BaseLayersCard";
import { InspectorChipButton } from "./InspectorChipButton";
import { InspectorIconGlyph } from "./InspectorIconGlyph";
import { SidetextRow } from "./SidetextRow";
import styles from "./IllustrationsCard.module.css";

export interface IllustrationsCardProps {
  reportToken: string | null;
  structuresCount: number;
  illustrationsVersion: number;
  onBeginIllustration: () => void;
  presentation?: InspectorPresentation;
}

export function IllustrationsCard({
  reportToken,
  structuresCount,
  illustrationsVersion,
  onBeginIllustration,
  presentation = "inspector"
}: IllustrationsCardProps): ReactElement | null {
  const drawerRail = presentation === "drawer";
  // The card surfaces purchased illustrations and the "Create illustration"
  // entry point. It used to require a placed structure (structuresCount > 0)
  // to render at all, which silently hid already-purchased renders from a
  // recipient opening a shared link with no structures present. The create
  // flow now auto-places a structure on demand (see main.ts
  // beginIllustrationOrAutoPlace), so the only requirement to show the card
  // is an active report token. `structuresCount` is retained on the props
  // for callers but no longer gates visibility.
  const enabled = Boolean(reportToken);
  const [rows, setRows] = useState<RenderPurchaseWire[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !reportToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api
      .listRenders(reportToken)
      .then((out) => {
        if (cancelled) return;
        setRows(out.renders);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load renders.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reportToken, illustrationsVersion]);

  if (!enabled) return null;

  return (
    <div>
      {rows.length === 0 && !loading ? (
        <p className={styles.emptyState}>
          Generate a conceptual rendering from your current camera angle and structure setup.
        </p>
      ) : null}
      {loading ? <p className={styles.status}>Loading illustrations...</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      {rows.length > 0 ? (
        <div className={styles.list}>
          {rows.map((row) => (
            <div className={styles.item} key={row.id}>
              <div className={styles.thumbWrap}>
                {row.thumbUrl ? (
                  <img
                    className={styles.thumb}
                    src={row.thumbUrl}
                    alt="Illustration thumbnail"
                  />
                ) : (
                  <div className={styles.thumb} aria-hidden />
                )}
                <InspectorChipButton
                  className={styles.downloadOverlay}
                  drawerRail={drawerRail}
                  aria-label={
                    row.status === "ready" ? "Download illustration" : "Illustration processing"
                  }
                  disabled={row.status !== "ready" || !row.downloadUrl}
                  onClick={() => {
                    if (row.downloadUrl) {
                      triggerDownload(row.downloadUrl, `${row.id}.png`);
                    }
                  }}
                >
                    <InspectorIconGlyph
                      src={downloadIcon}
                      size={16}
                      className={styles.downloadIcon}
                    />
                </InspectorChipButton>
              </div>
              <SidetextRow
                label={styleLabel(row.houseStyle)}
                secondaryLabel={`${styleLabel(row.landscapeStyle)} · ${styleLabel(row.season)}`}
                sideText={new Date(row.createdAt).toLocaleDateString()}
                monoSideText
                drawerRail={drawerRail}
              />
            </div>
          ))}
        </div>
      ) : null}
      <InspectorChipButton
        fullWidth
        drawerRail={drawerRail}
        className={styles.createChip}
        onClick={onBeginIllustration}
      >
        Create illustration
      </InspectorChipButton>
    </div>
  );
}

function styleLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m: string) => m.toUpperCase());
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
  }, 0);
}
